import { useGet } from "@albertine/api-client";
import type { LmtErrorResponse } from "@albertine/types/openapi";
import type { AgentListItem, UpdateAgentRequest } from "@albertine/types/openapi/lmt";
import { ConfirmDialog } from "@prototypes/modal/ConfirmDialog";
import { PageToolbar } from "@prototypes/page-toolbar/PageToolbar";
import { Plus, Users } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
    useCreateAgent,
    useDisableAgent,
    useEnableAgent,
    useUpdateAgent,
    useUpdateAgentPermissionGroup,
} from "../../common/hooks/useAgents";
import {
    useAgentTeams,
    useAssignAgentToTeam,
    useSyncAgentTeams,
} from "../../common/hooks/useAgentTeams";
import { AlbertineServiceAgentSenderId } from "../../common/validate";
import { EventEmitter } from "../../EventEmitter";
import { useClaims } from "../../utils/claims.util";
import { logErrorAndShowToast } from "../../utils/window.util";
import { AddAgentDialog } from "./AddAgentDialog";
import { type AgentStatus, AgentsTable } from "./AgentsTable";
import { EditTeamsDialog } from "./EditTeamsDialog";

const ALL_TAB = "All";
const STATUS_TABS = ["Active", "Inactive", ALL_TAB] as const;
type StatusFilter = (typeof STATUS_TABS)[number];

type PendingStatusChange = {
    agentId: string;
    nextStatus: AgentStatus;
};

type AgentDialogState = { mode: "create" } | { mode: "edit"; agent: AgentListItem };

export function AgentsList() {
    const { data: allAgentsData } = useGet("getAllAgents", {});
    const agents = allAgentsData?.results ?? [];

    const { uid, isAdmin, hasScope } = useClaims();
    const canManageAgents = isAdmin || hasScope("admin:updateAgent");
    const teams = useAgentTeams();

    const { data: permissionGroupsData } = useGet(
        "listPermissionGroups",
        {},
        { enabled: canManageAgents },
    );
    const permissionGroups = permissionGroupsData?.results ?? [];

    const [statusFilter, setStatusFilter] = useState<StatusFilter>("Active");
    const [isEditingTeams, setIsEditingTeams] = useState(false);
    const [agentDialogState, setAgentDialogState] = useState<AgentDialogState | null>(
        null,
    );
    const [pendingStatusChange, setPendingStatusChange] =
        useState<PendingStatusChange | null>(null);

    const onMutationError = (error: unknown) => {
        const message =
            error instanceof Error
                ? error.message
                : ((error as LmtErrorResponse)?.error ?? "Failed to update agent");
        logErrorAndShowToast(error, message);
    };

    const { mutate: updatePermissionGroup } = useUpdateAgentPermissionGroup({
        onError: onMutationError,
    });
    const { mutate: disableAgentMutation } = useDisableAgent({
        onError: onMutationError,
    });
    const { mutate: enableAgentMutation } = useEnableAgent({
        onError: onMutationError,
    });
    const { mutate: assignAgentToTeam } = useAssignAgentToTeam({
        onError: onMutationError,
    });
    const { mutate: syncAgentTeams, isPending: isSyncingTeams } = useSyncAgentTeams({
        onError: onMutationError,
        onSuccess: () => setIsEditingTeams(false),
    });
    const { mutate: createAgent, isPending: isCreatingAgent } = useCreateAgent({
        onError: onMutationError,
        onSuccess: () => {
            setAgentDialogState(null);
            if (statusFilter === "Inactive") setStatusFilter("Active");
            EventEmitter.emit({
                name: "showToastMessage",
                params: { message: "Agent created", type: "info" },
            });
        },
    });
    const { mutateAsync: updateAgentAsync, isPending: isUpdatingAgent } =
        useUpdateAgent({
            onError: onMutationError,
        });

    const canAssignTeam = (agent: AgentListItem) =>
        canManageAgents && agent.id !== AlbertineServiceAgentSenderId;

    const canEditAgent = (agent: AgentListItem) =>
        canManageAgents && agent.id !== AlbertineServiceAgentSenderId;

    const canUpdateAgent = (agent: AgentListItem) =>
        canManageAgents &&
        // The service agent is special and should not be changed.
        agent.id !== AlbertineServiceAgentSenderId &&
        // Allowing to change permissions for self (in the UI) can easily lead to
        // accidental lockouts, so hide the dropdown for self.
        agent.id !== uid;

    function applyStatusChange(agentId: string, nextStatus: AgentStatus) {
        if (nextStatus === "Inactive") {
            disableAgentMutation({ param: { agentId } });
            return;
        }
        enableAgentMutation({ param: { agentId } });
    }

    function requestStatusChange(agent: AgentListItem, nextStatus: AgentStatus) {
        const currentStatus: AgentStatus = agent.disabled ? "Inactive" : "Active";
        if (nextStatus === currentStatus) return;

        if (statusFilter !== ALL_TAB && nextStatus !== statusFilter) {
            setPendingStatusChange({ agentId: agent.id, nextStatus });
            return;
        }

        applyStatusChange(agent.id, nextStatus);
    }

    function confirmStatusChange() {
        if (!pendingStatusChange) return;
        applyStatusChange(pendingStatusChange.agentId, pendingStatusChange.nextStatus);
        setPendingStatusChange(null);
    }

    async function saveEditedAgent(agentId: string, request: UpdateAgentRequest) {
        try {
            await updateAgentAsync({
                param: { agentId },
                json: request,
            });
            setAgentDialogState(null);
            EventEmitter.emit({
                name: "showToastMessage",
                params: { message: "Agent updated", type: "info" },
            });
        } catch {
            // onError on the mutations already surfaces a toast.
        }
    }

    const activeCount = agents.filter((agent) => !agent.disabled).length;
    const countByTab: Record<StatusFilter, number> = {
        Active: activeCount,
        Inactive: agents.length - activeCount,
        All: agents.length,
    };

    // Non-managers only ever see active agents.
    const visibleTabs = canManageAgents ? STATUS_TABS : (["Active"] as const);

    const filteredAgents =
        statusFilter === ALL_TAB
            ? agents
            : agents.filter((agent) =>
                  statusFilter === "Active" ? !agent.disabled : agent.disabled,
              );

    const pendingStatusChangeAgent = pendingStatusChange
        ? (agents.find((agent) => agent.id === pendingStatusChange.agentId) ?? null)
        : null;

    return (
        <div className="tab flex h-full min-h-0 flex-1 flex-col bg-white">
            <PageToolbar
                title="Agents"
                count={agents.length}
                actions={
                    canManageAgents ? (
                        <>
                            <Button
                                variant="outline"
                                onClick={() => setIsEditingTeams(true)}
                            >
                                <Users data-icon="inline-start" />
                                Edit teams
                            </Button>
                            <Button
                                onClick={() => setAgentDialogState({ mode: "create" })}
                            >
                                <Plus data-icon="inline-start" />
                                New Agent
                            </Button>
                        </>
                    ) : undefined
                }
                tabs={visibleTabs.map((tab) => ({
                    value: tab,
                    label: tab,
                    count: countByTab[tab],
                }))}
                tabValue={statusFilter}
                onTabChange={(value) => setStatusFilter(value as StatusFilter)}
            />

            <AgentsTable
                agents={filteredAgents}
                teams={teams}
                permissionGroups={permissionGroups}
                emptyMessage={
                    statusFilter === ALL_TAB
                        ? "No agents yet."
                        : `No ${statusFilter.toLowerCase()} agents.`
                }
                canUpdateAgent={canUpdateAgent}
                canAssignTeam={canAssignTeam}
                canEditAgent={canEditAgent}
                onEditAgent={(agent) => setAgentDialogState({ mode: "edit", agent })}
                onAssignTeam={(agentId, teamId) =>
                    assignAgentToTeam({
                        param: { agentId },
                        json: { teamId },
                    })
                }
                onUpdatePermissionGroup={(agentId, permissionGroup) =>
                    updatePermissionGroup({
                        param: { agentId },
                        json: { permissionGroup },
                    })
                }
                onRequestStatusChange={requestStatusChange}
            />

            {isEditingTeams ? (
                <EditTeamsDialog
                    open
                    teams={teams}
                    isSaving={isSyncingTeams}
                    onClose={() => setIsEditingTeams(false)}
                    onSave={(nextTeams) =>
                        syncAgentTeams({ json: { teams: nextTeams } })
                    }
                />
            ) : null}

            {agentDialogState ? (
                <AddAgentDialog
                    key={
                        agentDialogState.mode === "edit"
                            ? agentDialogState.agent.id
                            : "create"
                    }
                    open
                    teams={teams}
                    isSaving={isCreatingAgent || isUpdatingAgent}
                    agent={
                        agentDialogState.mode === "edit" ? agentDialogState.agent : null
                    }
                    onClose={() => setAgentDialogState(null)}
                    onCreate={(request) => createAgent({ json: request })}
                    onUpdate={saveEditedAgent}
                />
            ) : null}

            <ConfirmDialog
                open={!!pendingStatusChange}
                title={`Mark ${pendingStatusChangeAgent?.name} as ${pendingStatusChange?.nextStatus.toLowerCase()}?`}
                description={statusChangeDescription(
                    pendingStatusChangeAgent?.name,
                    pendingStatusChange?.nextStatus,
                )}
                confirmLabel={`Mark as ${pendingStatusChange?.nextStatus.toLowerCase()}`}
                onConfirm={confirmStatusChange}
                onDismiss={() => setPendingStatusChange(null)}
            />
        </div>
    );
}

function statusChangeDescription(
    fullName: string | undefined,
    nextStatus: AgentStatus | undefined,
): string {
    const name = fullName ?? "This agent";
    const tabNote = `${name} will move to the ${nextStatus?.toLowerCase()} tab.`;

    if (nextStatus === "Inactive") {
        return `${tabNote} They can no longer use the system.`;
    }

    return tabNote;
}
